import type { Manifest } from './base/Field'
import HyCollection from './base/Collection'
import HyModel from './base/Model'
import Item from './Item'
import { required, length } from './base/Validation'

export default class ItemCollection extends HyModel {
  collectionItems = new CollectionItems()

  override options() {
    return {
      identifier: 'slug',
      remoteModel: 'Collection',
    }
  }

  override manifest(): Manifest {
    return {
      name: {
        validation: required.and(length(1, 100))
      },
      description: {
        label: 'Description (optional)',
        type: 'text',
        options: {
          rows: 3
        }
      },
      tags: {
        type: 'tags',
        local: true
      },
      tag_list: {
        type: 'virtual',
        options: {
          serialize: (model: any) => model.get('tags')?.join(',') ?? ''
        }
      },
      public: {
        label: 'Shared',
        type: 'switch',
        default: false,
        options: {
          help: 'All collections can be shared with other users. Public collections are listed in the Collections Directory.'
        }
      },
      broken: {},
      image: {},
      item_count: {},
      creator_name: {},
      creator_id: {},
      items: {}
    }
  }

  // Flags for display
  get flags() {
    // Item count
    const flags: Flags = [
      { label: this.item_count, color: 'primary' }
    ]
    // Public
    if (this.get('public')) {
      flags.push({ label: 'Shared', color: 'green' })
    }
    // Broken
    if (this.get('broken')) {
      flags.push({ label: 'Broken', color: 'error' })
    }
    return flags
  }

  // Parse item lists as CollectionItems
  override assign(attributes: Record<string, any>) {
    // Check for items and add them to the collection,
    // or clear the collection if there are no items
    if (attributes.items && isArray(attributes.items)) {
      if (attributes.items.length > 0) {
        this.collectionItems.add(attributes.items)
      } else {
        this.collectionItems.clearModels()
      }
    }
    super.assign(attributes)
  }

  // ----------------------------
  // Proxy for CollectionItems ==
  // ----------------------------

  get models() {
    return this.collectionItems.models as Item[]
  }

  clearModels() {
    this.collectionItems.clearModels()
  }

  find(query: Record<string, any>) {
    return this.collectionItems.find(query) as Item
  }

  exists(query: Record<string, any>) {
    return this.collectionItems.exists(query) as boolean
  }
}

// Utility class to store collection items as HyModels
class CollectionItems extends HyCollection {
  override options() {
    return { model: Item, }
  }
}
